import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewPanel } from "@/components/admin/OverviewPanel";
import { InquiriesPanel } from "@/components/admin/InquiriesPanel";
import { ClientsPanel } from "@/components/admin/ClientsPanel";
import { ContractsPanel } from "@/components/admin/ContractsPanel";
import { InvoicesPanel } from "@/components/admin/InvoicesPanel";
import { TemplatesPanel } from "@/components/admin/TemplatesPanel";
import { CalendarPanel } from "@/components/admin/CalendarPanel";

const ADMIN_TABS = [["overview", "Overview"], ["inquiries", "Inquiries"], ["calendar", "Calendar"], ["clients", "Clients"], ["contracts", "Contracts"], ["invoices", "Invoices"], ["templates", "Templates"]];
const BOOKING_TABS = [["inquiries", "Inquiries"], ["calendar", "Calendar"]];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const tabs = isAdmin ? ADMIN_TABS : BOOKING_TABS;
  const [inquiries, setInquiries] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState(isAdmin ? "overview" : "inquiries");
  const [newContractFor, setNewContractFor] = useState(null);
  const [newInvoiceFor, setNewInvoiceFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const i = await api.get("/admin/inquiries");
      setInquiries(Array.isArray(i.data) ? i.data : []);
      if (!isAdmin) return;
      const [c, t] = await Promise.all([api.get("/admin/clients"), api.get("/admin/templates")]);
      setClients(Array.isArray(c.data) ? c.data : []);
      setTemplates(Array.isArray(t.data) ? t.data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const clearContract = useCallback(() => setNewContractFor(null), []);
  const clearInvoice = useCallback(() => setNewInvoiceFor(null), []);
  const addClient = (c) => setClients((xs) => (xs.some((x) => x.id === c.id) ? xs : [...xs, c].sort((a, b) => a.name.localeCompare(b.name))));

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F5EFE6]" data-testid="admin-dashboard">
      <header className="border-b border-[#2E2A24]">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img src="/brand/rco-white.webp" alt="RCo" className="h-8" />
            <span className="font-mono-rc text-[11px] uppercase tracking-[0.25em] text-zinc-400" data-testid="admin-role-label">{isAdmin ? "Admin" : "Booking"}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden text-sm text-zinc-400 lg:block">{user?.email}</span>
            <Link to="/" className="flex items-center gap-2 rounded-full border border-[#2E2A24] px-4 py-2 text-sm transition-[border-color] hover:border-[#C4832B]" data-testid="admin-back-to-site-btn">
              <ArrowUpRight className="h-4 w-4" /> <span className="hidden sm:inline">Back to website</span><span className="sm:hidden">Website</span>
            </Link>
            <button onClick={logout} className="flex items-center gap-2 rounded-full border border-[#2E2A24] px-4 py-2 text-sm hover:border-[#C4832B]" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap bg-[#121215]">
            {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
          {isAdmin && (
            <TabsContent value="overview" className="mt-6">
              <OverviewPanel onOpenTab={setTab} />
            </TabsContent>
          )}
          <TabsContent value="inquiries" className="mt-6">
            <InquiriesPanel items={inquiries} setItems={setInquiries} canMakeClient={isAdmin} onClientCreated={(c) => { addClient(c); setTab("clients"); }} />
          </TabsContent>
          <TabsContent value="calendar" className="mt-6">
            <CalendarPanel />
          </TabsContent>
          {isAdmin && (
            <>
              <TabsContent value="clients" className="mt-6">
                <ClientsPanel clients={clients} setClients={setClients} onNewContract={(id) => { setNewContractFor(id); setTab("contracts"); }} onNewInvoice={(id) => { setNewInvoiceFor(id); setTab("invoices"); }} />
              </TabsContent>
              <TabsContent value="contracts" className="mt-6">
                <ContractsPanel clients={clients} templates={templates} openNewFor={newContractFor} clearOpenNew={clearContract} />
              </TabsContent>
              <TabsContent value="invoices" className="mt-6">
                <InvoicesPanel clients={clients} openNewFor={newInvoiceFor} clearOpenNew={clearInvoice} />
              </TabsContent>
              <TabsContent value="templates" className="mt-6">
                <TemplatesPanel templates={templates} setTemplates={setTemplates} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}
